import { Suspense, useEffect, useState, useMemo } from "react"
import classNames from "classnames"

import useFullHeight from "web/hooks/behaviour/useFullHeight"
import { useGlobalSearch } from "web/hooks/data"
import { isNonEmptyArray } from "web/services/helpers"

import { Loading } from "web/components"

import ResultsForType from "./ResultsForType"
import ResultTypeSelector from "./ResultTypeSelector"

import PlaceResults from "./PlaceResults"

const SEARCHABLE_SUBJECT_TYPES = [
  "Deal",
  "Asset",
  "Company",
  "Portfolio",
  "Contact",
]

function getResultsByType(subjectMatches) {
  const resultsByType = SEARCHABLE_SUBJECT_TYPES.map((type) => {
    const resultsForType = subjectMatches.filter(
      (result) => result.subject_type === type
    )

    return {
      type,
      count: resultsForType.length,
      results: resultsForType,
    }
  })

  return resultsByType.filter((results) => results.count !== 0)
}

function ResultsMessage({ message }) {
  return (
    <div className="mt-5 grid place-items-center text-neutral-6">{message}</div>
  )
}

function Results({ query, onResultSelected }) {
  const { place_matches: placeMatches, subject_matches: subjectMatches } =
    useGlobalSearch(query)

  const hasPlaceMatches = isNonEmptyArray(placeMatches)
  const hasSubjectMatches = isNonEmptyArray(subjectMatches)
  const hasMatches = hasPlaceMatches || hasSubjectMatches

  const resultsByTypes = useMemo(
    () => getResultsByType(subjectMatches),
    [subjectMatches]
  )

  const [resultType, setResultType] = useState(
    hasSubjectMatches ? resultsByTypes[0].type : null
  )

  useEffect(() => {
    const hasMatchesForType = resultsByTypes.some(
      (el) => el.type === resultType
    )
    if (!hasMatchesForType && hasSubjectMatches) {
      setResultType(resultsByTypes[0].type)
    }
  }, [resultType, resultsByTypes, hasSubjectMatches])

  if (!hasMatches) {
    return <ResultsMessage message="No results found." />
  }

  return (
    <div className="h-full pb-8">
      {hasPlaceMatches && (
        <PlaceResults
          results={placeMatches}
          onResultSelected={onResultSelected}
        />
      )}
      <div className="hidden lg:block">
        <ResultTypeSelector
          resultType={resultType}
          resultsByTypes={resultsByTypes}
          onChange={setResultType}
        />
      </div>

      {resultsByTypes.map(({ type, results }) => {
        return (
          <div
            key={type}
            className={classNames("mt-3", { "lg:hidden": type !== resultType })}
          >
            <ResultsForType
              type={type}
              results={results}
              onResultSelected={onResultSelected}
            />
          </div>
        )
      })}
    </div>
  )
}

const MIN_QUERY_LENGTH = 3

export default function ResultsWrapper({ query, onComplete }) {
  useFullHeight({ enabled: true, elementId: "global-search-results" })

  const hasValidQuery = query.length >= MIN_QUERY_LENGTH

  return (
    <div
      id="global-search-results"
      className="fixed left-0 top-12 z-40 w-full overflow-scroll bg-neutral-10 px-5 lg:px-20"
    >
      <div>
        {!hasValidQuery && (
          <ResultsMessage message="Your search query must contain at least three letters." />
        )}
        {hasValidQuery && (
          <Suspense fallback={<Loading />}>
            <Results query={query} onResultSelected={onComplete} />
          </Suspense>
        )}
      </div>
    </div>
  )
}
